import React from 'react';

interface Column {
  key: string;
  title?: string;  // Make title optional for backwards compatibility
  label?: React.ReactNode;  // New label field for club dashboard
  clickable?: boolean;
  render?: (value: any, row: any, index: number) => React.ReactNode;
  cellClassName?: (value: any, row: any, index: number) => string;
}

interface DataTableProps {
  columns: Column[];
  data: any[];
  className?: string;
  tableClassName?: string;
  tableStyle?: React.CSSProperties;
  striped?: boolean;
  hoverable?: boolean;
  onColumnClick?: (columnKey: string) => void;
  totalsRow?: any;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  className = '',
  tableClassName = '',
  tableStyle,
  striped = true,
  hoverable = true,
  onColumnClick,
  totalsRow
}) => {
  const handleColumnClick = (column: Column) => {
    if (column.clickable && onColumnClick) {
      onColumnClick(column.key);
    }
  };

  return (
    <div className={`table-container ${className}`}>
      <table
        className={`table ${tableClassName}`}
        style={{ direction: 'rtl', ...(tableStyle || {}) }}
      >
        <thead className="table-header !bg-gray-100 dark:!bg-gray-800">
          <tr>
            {columns.map((column) => {
              const columnTitle = column.label || column.title || column.key;
              return (
                <th
                  key={column.key}
                  className={`table-cell text-xs font-bold uppercase tracking-wider px-4 py-3 ${
                    column.clickable
                      ? 'cursor-pointer hover:bg-bg-tertiary transition-colors group'
                      : ''
                  }`}
                  style={{ direction: 'rtl' }}
                  onClick={() => handleColumnClick(column)}
                >
                  <div className="flex items-center justify-center gap-1">
                    {columnTitle}
                    {column.clickable && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                        ⇅
                      </span>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={`${striped ? (rowIndex % 2 === 0 ? '!bg-white dark:!bg-gray-900' : '!bg-gray-50 dark:!bg-gray-800') : ''} ${hoverable ? 'hover:!bg-blue-50 dark:hover:!bg-gray-700' : ''} transition-colors`}
            >
              {columns.map((column) => {
                const value = row[column.key];
                return (
                  <td
                    key={column.key}
                    className={`table-cell text-sm font-bold text-center px-4 py-3 rounded-sm${column.cellClassName ? ` ${column.cellClassName(value, row, rowIndex)}` : ''}`}
                    style={{ direction: 'rtl' }}
                  >
                    {column.render ? column.render(value, row, rowIndex) : value}
                  </td>
                );
              })}
            </tr>
          ))}
          
          {/* Totals Row */}
          {totalsRow && (
            <tr className="table-row bg-gray-200 dark:bg-gray-700 font-bold border-t-2 border-gray-300 dark:border-gray-600">
              {columns.map((column, index) => {
                if (index === 0) {
                  return (
                    <td
                      key={column.key}
                      className="table-cell text-sm font-bold text-center px-4 py-3"
                      style={{ direction: 'rtl' }}
                    >
                      مجموع
                    </td>
                  );
                }
                
                const value = totalsRow[column.key];
                
                // Handle special cases for totals row
                if (['priceGrowth', 'basketGrowth'].includes(column.key)) {
                  return (
                    <td
                      key={column.key}
                      className="table-cell text-sm font-bold text-center px-4 py-3"
                      style={{ direction: 'rtl' }}
                    >
                      -
                    </td>
                  );
                }
                
                return (
                  <td
                    key={column.key}
                    className="table-cell text-sm font-bold text-center px-4 py-3"
                    style={{ direction: 'rtl' }}
                  >
                    {column.render && value !== undefined ? column.render(value, totalsRow, -1) : (value || '-')}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
